<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <form class="" @submit.prevent="">
      <div>
        <base-input
          v-if="
            salesInvoiceItemModel.salesInvoiceable.type.includes('products')
          "
          :label="`${$t(`COMMON.PRODUCT`)} (*)`"
        >
          <product-selector
            :filterOrganization="salesInvoice.organization.id"
            :filterIds="getOrderIds()"
            :productObject="salesInvoiceItemModel.salesInvoiceable"
            :allowNone="false"
            :showAll="false"
            :filterWarehouse="
              salesInvoice.sourceWarehouse
                ? salesInvoice.sourceWarehouse.id
                : null
            "
            @productChanged="productChanged"
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.salesInvoiceable" />
      </div>

      <base-input
        :label="`${$t('COMMON.CODE')} (*)`"
        :placeholder="$t('COMMON.CODE')"
        v-model="salesInvoiceItemModel.code"
        input-classes="form-control-alternative"
        v-if="!salesInvoiceItemModel.salesInvoiceable.type"
      >
      </base-input>
      <validation-error
        v-if="!salesInvoiceItemModel.salesInvoiceable.type"
        :errors="apiValidationErrors.code"
      />

      <base-input
        :label="$t('COMMON.EXCERPT')"
        :placeholder="$t('COMMON.EXCERPT')"
        input-classes="form-control-alternative"
      >
        <html-editor
          v-model="salesInvoiceItemModel.excerpt"
          @change="onFormChanged()"
        >
        </html-editor>
      </base-input>
      <validation-error :errors="apiValidationErrors.excerpt" />

      <div class="row px-3">
        <div class="col-4">
          <base-input
            :label="`${$t('COMMON.QUANTITY')} (*)`"
            :placeholder="$t('COMMON.QUANTITY')"
            v-model="salesInvoiceItemModel.quantity"
            type="number"
            step="1"
            input-classes="form-control-alternative"
          >
          </base-input>
          <validation-error :errors="apiValidationErrors.quantity" />
        </div>
        <div class="col-4">
          <base-input
            :label="$t('COMMON.UNIT_OF_MEASURE_UNIT')"
            :placeholder="$t('COMMON.UNIT_OF_MEASURE_UNIT')"
            input-classes="form-control-alternative"
          >
            <unit-of-measure-unit-selector
              :filterOrganization="salesInvoice.organization.id"
              :filterUnitOfMeasure="unitOfMeasure?.id"
              :unit="salesInvoiceItemModel.unitOfMeasureUnit?.id"
              :allowNone="false"
              :showAll="false"
              @unitChanged="
                (unit) => {
                  unitOfMeasureUnitChanged(unit);
                }
              "
            />
          </base-input>
          <validation-error :errors="apiValidationErrors.taxGroups" />
        </div>
        <div class="col-4">
          <base-input
            :label="`${$t('COMMON.UNIT_PRICE')} (*)`"
            :placeholder="$t('COMMON.UNIT_PRICE')"
            v-model="salesInvoiceItemModel.unit_price"
            type="number"
            step="0.0001"
            input-classes="form-control-alternative"
          >
          </base-input>
          <validation-error :errors="apiValidationErrors.unit_price" />
        </div>
      </div>

      <template v-if="!salesInvoice.has_no_taxes">
        <base-input
          :label="$t('COMMON.TAX_GROUPS')"
          :placeholder="$t('COMMON.TAX_GROUPS')"
          input-classes="form-control-alternative"
        >
          <tax-groups-selector
            :taxGroups="salesInvoiceItemModel.taxGroups"
            @taxGroupsChanged="
              (taxGroups) => {
                salesInvoiceItemModel.taxGroups = taxGroups;
              }
            "
          />
          <validation-error :errors="apiValidationErrors.taxGroups" />
        </base-input>
      </template>

      <base-input
        :label="$t('COMMON.DISCOUNT_PERCENT')"
        :placeholder="$t('COMMON.DISCOUNT_PERCENT')"
        v-model="salesInvoiceItemModel.discount"
        type="number"
        step="0.0001"
        input-classes="form-control-alternative"
      >
      </base-input>
      <validation-error :errors="apiValidationErrors.discount" />
    </form>

    <template slot="footer">
      <button
        v-if="!salesInvoiceItemModel.id"
        type="submit"
        class="btn btn-primary"
        @click="submitSalesInvoiceItemForm"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("SALES_INVOICES.ADD_SALES_INVOICE_ITEM") }}
      </button>

      <base-button
        v-if="salesInvoiceItemModel.id"
        native-type="submit"
        type="primary"
        class="new-sales-invoice-item--add"
        @click="submitSalesInvoiceItemForm"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("SALES_INVOICES.UPDATE_SALES_INVOICE_ITEM") }}
      </base-button>

      <base-button
        v-if="salesInvoiceItemModel.id"
        type="danger"
        @click="deleteSalesInvoiceItem(salesInvoiceItemModel.id)"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("SALES_INVOICES.DELETE_SALES_INVOICE_ITEM") }}
      </base-button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closeSalesInvoiceItemModal"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import {
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import { cloneDeep } from "lodash";
import formMixin from "@/mixins/form-mixin";
import requestErrorMixin from "@/mixins/request-error-mixin";
import ValidationError from "@/components/ValidationError.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import ProductSelector from "@/components/ProductSelector.vue";
import TaxGroupsSelector from "@/components/TaxGroupsSelector.vue";
import UnitOfMeasureUnitSelector from "@/components/UnitOfMeasureUnitSelector.vue";
import defaultSalesInvoiceItem from "../defaultSalesInvoiceItem";

export default {
  name: "sales-invoice-view-details-sales-invoice-item-form",

  components: {
    HtmlEditor,
    ValidationError,
    ProductSelector,
    TaxGroupsSelector,
    UnitOfMeasureUnitSelector,
    [Tooltip.name]: Tooltip,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [formMixin, requestErrorMixin],

  props: [
    "salesInvoice",
    "salesInvoiceItem",
    "showModal",
    "loading",
    "formErrors",
  ],

  data() {
    return {
      all_salesInvoiceables: [],
      salesInvoiceItemModel: cloneDeep(defaultSalesInvoiceItem),
      unitOfMeasure: null,
    };
  },

  computed: {},

  methods: {
    getOrderIds() {
      if (!this.salesInvoice.salesOrder) {
        return null;
      }
      if (this.salesInvoice.salesOrder.invoicingItemsState) {
        const remainingProductsIds = Object.values(
          this.salesInvoice.salesOrder.invoicingItemsState.remainingItems
        ).map((item) => item.product_id);
        if (remainingProductsIds.length > 0) {
          return remainingProductsIds;
        }
      }
      return null;
    },

    closeSalesInvoiceItemModal() {
      this.$emit("onCloseSalesInvoiceItemModal", true);
    },

    submitSalesInvoiceItemForm() {
      const salesInvoiceItemData = cloneDeep(this.salesInvoiceItemModel);
      salesInvoiceItemData.unit_price = parseFloat(
        salesInvoiceItemData.unit_price
      );
      salesInvoiceItemData.quantity = parseInt(
        salesInvoiceItemData.quantity,
        10
      );
      salesInvoiceItemData.discount = parseFloat(salesInvoiceItemData.discount);
      if (!salesInvoiceItemData.salesInvoiceable.id) {
        salesInvoiceItemData.salesInvoiceable = null;
      }

      this.$emit("onSubmitSalesInvoiceItemForm", salesInvoiceItemData);
    },

    deleteSalesInvoiceItem(salesInvoiceItemId) {
      this.$emit("onDeleteSalesInvoiceItem", salesInvoiceItemId);
    },

    async unitOfMeasureUnitChanged(unit) {
      const productPricing = await this.$getProductUnitOfMeasureUnitPrice(
        this.salesInvoiceItemModel.salesInvoiceable,
        unit.id
      );
      this.salesInvoiceItemModel.unitOfMeasureUnit = unit;
      this.salesInvoiceItemModel.unit_price = productPricing.selling_price;
    },

    productChanged(productId, product) {
      this.salesInvoiceItemModel.salesInvoiceable = product;
      this.salesInvoiceItemModel.excerpt = product.denomination;

      if (product.warehouseProduct) {
        this.salesInvoiceItemModel.unit_price =
          product.warehouseProduct.selling_price;
        this.salesInvoiceItemModel.code =
          product.warehouseProduct.sku || product.sku || product.code;
      } else {
        this.salesInvoiceItemModel.unit_price = product.selling_price;
        this.salesInvoiceItemModel.code = product.code;
      }

      if (product.unitOfMeasure) {
        this.salesInvoiceItemModel.unitOfMeasureUnit =
          product.unitOfMeasure.referenceUnit;
      }
      if (!this.salesInvoice.has_no_taxes) {
        this.salesInvoiceItemModel.taxGroups = product.taxGroups;
      } else {
        this.salesInvoiceItemModel.taxGroups = [];
      }
      this.unitOfMeasure = product.unitOfMeasure;
    },
  },

  mounted() {},

  watch: {
    salesInvoiceItem(salesInvoiceItem) {
      if (salesInvoiceItem) {
        this.salesInvoiceItemModel = cloneDeep(salesInvoiceItem);
      }
      this.resetApiValidation();
    },

    formErrors(errors) {
      if (errors) {
        this.setApiValidation(errors);
      }
    },
  },
};
</script>
